import { AxiosResponse } from 'axios-cad-nx';
import {
    ApiResultRequestAssistanceResponse,
    RequestAssistanceResource,
    RequestAssistanceResponse,
    RequestAssistanceView,
} from '@mark43/cad-api';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { RequestAssistanceFormDisplayType } from '../types';

interface RequestAssistanceApi {
    requestAssistance(data: RequestAssistanceFormDisplayType): void;
    requestAssistanceFormData: RequestAssistanceView | undefined;
    query: ReturnType<typeof useQuery>;
    mutation: UseMutationResult<
        AxiosResponse<ApiResultRequestAssistanceResponse, unknown>,
        unknown,
        RequestAssistanceFormDisplayType,
        unknown
    >;
}

export function useRequestAssistanceApi(
    agencyEventId: number,
    onSuccess: (resp: RequestAssistanceResponse) => unknown
): RequestAssistanceApi {
    const apiClient = useApiClient();

    const mutation = useMutation(
        ['request-assistance', {}],
        (form: RequestAssistanceFormDisplayType) => {
            return apiClient.request<RequestAssistanceResource.RequestAssistance>({
                method: 'POST',
                url: 'request_assistance',
                data: {
                    requestAssistanceForms: form.forms,
                    requestAssistanceSelections: {
                        copyAllInformation: form.copyAllInformation,
                        copyAttachments: form.copyAttachments,
                        useCurrentDateTime: form.useCurrentDateTime === 'true',
                    },
                },
            });
        },
        {
            onSuccess(data) {
                if (data.data.data) {
                    onSuccess(data.data.data);
                }
            },
        }
    );

    const { mutate } = mutation;

    const query = useQuery(
        ['request-assistance-form-data', agencyEventId],
        () => {
            return apiClient
                .request<RequestAssistanceResource.RequestAssistanceBootstrap>({
                    method: 'GET',
                    url: `request_assistance/${agencyEventId}/`,
                })
                .then((resp) => resp.data.data);
        },
        { refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false }
    );

    const { data: requestAssistanceFormData } = query;

    function requestAssistance(data: RequestAssistanceFormDisplayType): void {
        mutate(data);
    }

    return {
        query,
        mutation,
        requestAssistance,
        requestAssistanceFormData,
    };
}
